// app/components/provider/ThemeProvider.tsx
"use client";

import { darkTheme, lightTheme } from "@/styles/theme";
import createEmotionCache from "@/utils/createEmotionCache";
import { ThemeProvider as MUIThemeProvider, Theme, useMediaQuery } from "@mui/material";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v15-appRouter";
import CssBaseline from "@mui/material/CssBaseline";
import React, { createContext, useContext, useState, useEffect } from "react";
import AppBridge from "./AppBridge";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useThemeContext must be used within a ThemeProvider");
  return context;
}

const themes: Record<string, Theme> = {
  light: lightTheme,
  dark: darkTheme,
};

// 초기 테마를 계산하는 헬퍼 함수 (서버와 클라이언트에서 재사용 가능)
function getInitialTheme(initialTheme: string, prefersDarkMode: boolean): Theme {
  const savedTheme = typeof window !== "undefined" ? localStorage.getItem("theme") : null;
  const themeKey = savedTheme || initialTheme;
  return themes[themeKey === "system" ? (prefersDarkMode ? "dark" : "light") : themeKey] || lightTheme;
}

export default function ThemeProvider({
  children,
  initialTheme = "system",
}: {
  children: React.ReactNode;
  initialTheme?: string;
}) {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", { defaultMatches: false });
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme(initialTheme, prefersDarkMode));
  const cache = createEmotionCache();

  useEffect(() => {
    const newTheme = getInitialTheme(initialTheme, prefersDarkMode);
    const themeName = initialTheme === "system" ? (prefersDarkMode ? "dark" : "light") : initialTheme;
    document.documentElement.setAttribute("data-theme", themeName);
    setTheme(newTheme);
  }, [initialTheme, prefersDarkMode]);

  return (
    <AppRouterCacheProvider options={{ ...cache }}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
          <AppBridge />
        </MUIThemeProvider>
      </ThemeContext.Provider>
    </AppRouterCacheProvider>
  );
}
